/**
 * Delivery Throughput Analytics Page - Sprint 18
 * Shows work item throughput trends, velocity, and cycle time analytics
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, BarChart2, Activity, Clock, RefreshCw,
  Loader2, Calendar, Filter, ArrowUp, ArrowDown
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { toast } from 'sonner';
import { useFilters } from '../context/FilterContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, trend, trendUp, icon: Icon, color }) => (
  <div className="bg-white rounded-lg border p-5" data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className={`text-2xl font-bold ${color || 'text-gray-900'}`}>{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      {Icon && (
        <div className={`p-2 rounded-lg ${color ? `bg-${color.replace('text-', '')}/10` : 'bg-gray-100'}`}>
          <Icon className={`w-5 h-5 ${color || 'text-gray-500'}`} />
        </div>
      )}
    </div>
    {trend !== undefined && (
      <div className={`mt-2 flex items-center gap-1 text-sm ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
        {trendUp ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
        <span>{trend}% vs last period</span>
      </div>
    )}
  </div>
);

// Main Component
const DeliveryThroughput = () => {
  const { getFilterParams } = useFilters();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('12w'); // 4w, 12w, 6m, 12m
  
  // Load throughput data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const filterParams = getFilterParams();
      const res = await fetch(
        `${API_BASE}/analytics/v1/work-items/throughput?period=${period}&${new URLSearchParams(filterParams)}`,
        { headers: getAuthHeaders() }
      );
      
      if (res.ok) {
        const result = await res.json();
        setData(result);
      } else {
        // TENANT ISOLATION: No fallback to mock data - show empty state
        console.warn('[DeliveryThroughput] API returned non-OK status, showing empty state');
        setData(null);
      }
    } catch (err) {
      console.error('Failed to load throughput data:', err);
      // TENANT ISOLATION: No mock data fallback - show empty state
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period, getFilterParams]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Generate mock data for demo
  const generateMockData = () => {
    const weeks = parseInt(period) || 12;
    const weeklyData = [];
    const now = new Date();
    
    for (let i = weeks - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - (i * 7));
      weeklyData.push({
        week: `W${weeks - i}`,
        date: date.toISOString().split('T')[0],
        completed: Math.floor(Math.random() * 30) + 20,
        started: Math.floor(Math.random() * 35) + 25,
        wip: Math.floor(Math.random() * 15) + 10,
        cycle_time: parseFloat((Math.random() * 3 + 2).toFixed(1))
      });
    }
    
    const totalCompleted = weeklyData.reduce((sum, w) => sum + w.completed, 0);
    const avgCycleTime = weeklyData.reduce((sum, w) => sum + w.cycle_time, 0) / weeks;
    const lastPeriodCompleted = weeklyData.slice(-4).reduce((sum, w) => sum + w.completed, 0);
    const prevPeriodCompleted = weeklyData.slice(-8, -4).reduce((sum, w) => sum + w.completed, 0);
    
    return {
      summary: {
        total_completed: totalCompleted,
        avg_per_week: Math.round(totalCompleted / weeks),
        avg_cycle_time_days: avgCycleTime.toFixed(1),
        avg_lead_time_days: (avgCycleTime * 1.8).toFixed(1),
        velocity_trend: prevPeriodCompleted > 0 
          ? Math.round(((lastPeriodCompleted - prevPeriodCompleted) / prevPeriodCompleted) * 100)
          : 0,
        wip_avg: Math.round(weeklyData.reduce((sum, w) => sum + w.wip, 0) / weeks)
      },
      weekly_data: weeklyData,
      by_type: [
        { type: 'Story', count: Math.floor(totalCompleted * 0.5) },
        { type: 'Task', count: Math.floor(totalCompleted * 0.3) },
        { type: 'Bug', count: Math.floor(totalCompleted * 0.15) },
        { type: 'Feature', count: Math.floor(totalCompleted * 0.05) }
      ],
      by_priority: [
        { priority: 'Critical', count: Math.floor(totalCompleted * 0.1), avg_cycle: 1.5 },
        { priority: 'High', count: Math.floor(totalCompleted * 0.3), avg_cycle: 2.5 },
        { priority: 'Medium', count: Math.floor(totalCompleted * 0.4), avg_cycle: 3.5 },
        { priority: 'Low', count: Math.floor(totalCompleted * 0.2), avg_cycle: 5.0 }
      ]
    };
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  const { summary, weekly_data, by_type, by_priority } = data || {};
  
  return (
    <div className="min-h-screen bg-gray-100 p-6" data-testid="delivery-throughput-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-blue-600" />
            Delivery Throughput
          </h1>
          <p className="text-gray-500">Work item velocity, cycle time, and throughput analytics</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border rounded-lg px-3 py-2"
            data-testid="period-selector"
          >
            <option value="4w">Last 4 Weeks</option>
            <option value="12w">Last 12 Weeks</option>
            <option value="6m">Last 6 Months</option>
            <option value="12m">Last 12 Months</option>
          </select>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-white"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          title="Items Completed"
          value={summary?.total_completed || 0}
          subtitle={`${summary?.avg_per_week || 0} avg/week`}
          trend={Math.abs(summary?.velocity_trend || 0)}
          trendUp={summary?.velocity_trend >= 0}
          icon={BarChart2}
          color="text-blue-600"
        />
        <KPICard
          title="Cycle Time"
          value={`${summary?.avg_cycle_time_days || 0} days`}
          subtitle="Avg time in progress"
          icon={Clock}
          color="text-green-600"
        />
        <KPICard
          title="Lead Time"
          value={`${summary?.avg_lead_time_days || 0} days`}
          subtitle="Avg time to delivery"
          icon={Activity}
          color="text-purple-600"
        />
        <KPICard
          title="Work in Progress"
          value={summary?.wip_avg || 0}
          subtitle="Avg concurrent items"
          icon={TrendingUp}
          color="text-amber-600"
        />
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Throughput Trend */}
        <div className="bg-white rounded-lg border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Weekly Throughput Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={weekly_data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="completed" name="Completed" fill="#3b82f6" stroke="#2563eb" fillOpacity={0.3} />
              <Area type="monotone" dataKey="started" name="Started" fill="#10b981" stroke="#059669" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Cycle Time Trend */}
        <div className="bg-white rounded-lg border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Cycle Time Trend (Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={weekly_data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="cycle_time" name="Cycle Time" stroke="#8b5cf6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Breakdown Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Type */}
        <div className="bg-white rounded-lg border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Completed by Type</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={by_type} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="type" type="category" width={80} />
              <Tooltip />
              <Bar dataKey="count" name="Items" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {/* By Priority */}
        <div className="bg-white rounded-lg border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">By Priority (with Avg Cycle Time)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={by_priority}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="priority" />
              <YAxis yAxisId="left" orientation="left" stroke="#3b82f6" />
              <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="count" name="Items" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="avg_cycle" name="Avg Cycle (days)" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default DeliveryThroughput;
